/**
 * Product Transactions List
 * Product-specific table: shows Variant column, quantity in units (+/-),
 * from/to location for transfers. No PO/Invoice/cost columns.
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TablePagination, useTheme, alpha
} from '@mui/material';
import { TRANSACTION_TYPE_MAP, formatTransactionDate } from './transactionConstants';

const hSx = (theme) => ({
  fontWeight: 500,
  fontSize: '0.9rem',
  bgcolor: 'white',
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  color: 'primary.main',
  py: 2,
  px: 2,
  whiteSpace: 'nowrap'
});

const cSx = (theme) => ({
  py: 2.5,
  px: 2,
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
});

function TypeChip({ transactionType, theme }) {
  const typeInfo = TRANSACTION_TYPE_MAP[transactionType];
  if (!typeInfo) return <Chip label={transactionType || 'Unknown'} size="small" />;
  return (
    <Chip
      icon={typeInfo.icon}
      label={typeInfo.label}
      size="small"
      color={typeInfo.color}
      sx={{
        fontWeight: 500,
        fontSize: '0.8rem',
        height: 28,
        border: '2px solid',
        borderColor: theme.palette[typeInfo.color]?.main || theme.palette.primary.main
      }}
    />
  );
}

function QuantityDisplay({ tx }) {
  const quantity = tx.quantity ?? tx.quantity_change;
  if (quantity === null || quantity === undefined) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  const num    = parseFloat(quantity);
  const isNeg  = num < 0;
  const absQty = Math.abs(num);
  const color  = isNeg ? 'error.main' : 'success.main';
  const sign   = isNeg ? '-' : '+';

  // units_per_case is stored on the transaction row itself (via * select)
  const unitsPerCase = tx.units_per_case || 1;
  const showCases    = unitsPerCase > 1;

  if (!showCases) {
    return (
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color }}>
        {sign}{absQty.toLocaleString()} units
      </Typography>
    );
  }

  const cases          = Math.floor(absQty / unitsPerCase);
  const remainingUnits = absQty % unitsPerCase;

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color }}>
        {sign}{cases.toLocaleString()} {cases === 1 ? 'case' : 'cases'}
        {remainingUnits > 0 ? ` + ${remainingUnits.toLocaleString()} units` : ''}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {absQty.toLocaleString()} units total
      </Typography>
    </Box>
  );
}

function LocationDisplay({ tx }) {
  const isTransfer = tx.transaction_type === 'transfer';
  if (isTransfer) {
    const from = tx.from_location?.name || 'Unknown';
    const to   = tx.to_location?.name   || 'Unknown';
    return (
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
        {from} → {to}
      </Typography>
    );
  }
  const loc = tx.from_location?.name || tx.to_location?.name || '-';
  return (
    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
      {loc}
    </Typography>
  );
}

export default function ProductTransactionsList({
  transactions,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  timeFormat,
  onViewTransaction
}) {
  const theme = useTheme();

  return (
    <Paper sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hSx(theme), width: '14%' }}>Date</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '12%' }}>Type</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '18%' }}>Product</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '12%' }}>Variant</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '20%' }}>Location</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '12%' }}>Quantity</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '12%' }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, index) => (
              <TableRow
                key={tx.id}
                hover
                onClick={() => onViewTransaction(tx)}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  bgcolor: index % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.8),
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  }
                }}
              >
                {/* Date */}
                <TableCell sx={cSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                    {formatTransactionDate(tx.transaction_date || tx.created_at, timeFormat)}
                  </Typography>
                </TableCell>

                {/* Type */}
                <TableCell sx={cSx(theme)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <TypeChip transactionType={tx.transaction_type} theme={theme} />
                    {tx.po_number && (
                      <Chip label={`PO #${tx.po_number}`} size="small" variant="outlined"
                        sx={{ height: 18, fontSize: '0.65rem', borderColor: 'primary.main', color: 'primary.main' }} />
                    )}
                  </Box>
                </TableCell>

                {/* Product */}
                <TableCell sx={cSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color: 'text.primary' }}>
                    {tx.products?.name || 'Unknown'}
                  </Typography>
                  {tx.products?.sku && (
                    <Typography variant="caption" color="text.secondary">
                      SKU: {tx.products.sku}
                    </Typography>
                  )}
                </TableCell>

                {/* Variant */}
                <TableCell sx={cSx(theme)}>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
                    {tx.variants?.name || '-'}
                  </Typography>
                </TableCell>

                {/* Location */}
                <TableCell sx={cSx(theme)}>
                  <LocationDisplay tx={tx} />
                </TableCell>

                {/* Quantity */}
                <TableCell sx={cSx(theme)}>
                  <QuantityDisplay tx={tx} />
                </TableCell>

                {/* Notes */}
                <TableCell sx={cSx(theme)}>
                  {tx.notes?.startsWith('PI Delivery Deduction') ? (() => {
                    const piNumMatch  = tx.notes.match(/PI #([^\s|—]+)/);
                    const custMatch   = tx.notes.match(/Customer:\s*([^—|]+)/);
                    const compMatch   = tx.notes.match(/Company:\s*([^—|]+)/);
                    const piNumber    = piNumMatch?.[1]?.trim();
                    const customer    = custMatch?.[1]?.trim();
                    const company     = compMatch?.[1]?.trim();
                    return (
                      <Box>
                        <Chip
                          label={piNumber ? `PI #${piNumber}` : 'PI Delivery'}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 500, fontSize: '0.72rem', height: 20, mb: 0.5 }}
                        />
                        {customer && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                            {customer}
                          </Typography>
                        )}
                        {company && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                            {company}
                          </Typography>
                        )}
                      </Box>
                    );
                  })() : (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.85rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}
                      title={tx.notes || ''}
                    >
                      {tx.notes || '-'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6, px: 3 }}>
        <Paper sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden'
        }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[12, 24, 48]}
          />
        </Paper>
      </Box>
    </Paper>
  );
}
